import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { MUser } from "../models/user.model";
import { SecurityQuestion } from "../interfaces/User";
import {
  OrganizationUser,
  OrganizationUserMassive,
  Process,
  Organization,
  Evidence,
  Source
} from "../interfaces/Organization";
import { MOrganization } from "../models/organization.model";
import { GlobalsService } from "./globals";
import { MToolboxDashboard } from "../models/ToolboxDashboard.model";
import { MModuleEvaluation } from "../models/module-evaluation.model";
import { MProcess } from "../models/process.model";
import { async } from "q";
import { map } from "rxjs/operators";
import { MReportType } from "../models/report-type.model";
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: "root"
})
export class SieniService {
  constructor(
    private httpClient: HttpClient,
    private _global: GlobalsService
  ) {}
  public getCurrentUser =  new BehaviorSubject<MUser>(null);
  public user: MUser = null;
  public org: MOrganization = null;
  public toolbox: MToolboxDashboard = null;
  public processes: MProcess[] = null;
  private pass: string = null;

  private header(token: string = null): HttpHeaders {
    if (!token) {
      return new HttpHeaders({
        Authorization: "Bearer " + this.user.token,
        Accept: "application/json",
        "Content-Type": "application/json"
      });
    } else {
      return new HttpHeaders({
        Authorization: "Bearer " + token,
        Accept: "application/json",
        "Content-Type": "application/json"
      });
    }
  }

  /**
   * @author Carlos Albornoz
   * @name setUser
   * @description Guaradar un usuario y que este disponible para toda la aplicacion
   * @param newUser Hace referencia al nuevo usuario que se va a guardar
   */
  public setUser(newUser: MUser) {
    this.user = newUser;
    if (newUser) {
      sessionStorage.setItem("activeUser", JSON.stringify(this.user));
      this.getCurrentUser.next(newUser);
    } else {
      sessionStorage.removeItem("activeUser");
      this.getCurrentUser.next(null);
    }
  }

  /**
   * @author Carlos Albornoz
   * @name setOrg
   * @description Guarda una organizacion para que este disponible para tola la aplicacion
   * @param newOrg Hace referencia a la nueva organizacion a guardar
   */
  public setOrg(newOrg: MOrganization) {
    this.org = newOrg;
    if (newOrg) {
      sessionStorage.setItem("organization", JSON.stringify(this.org));
    } else {
      sessionStorage.removeItem("organization");
    }
  }

  public async getCurrentOrganization() {
    if (this.org) {
      return await this.org;
    } else if (sessionStorage.getItem("organization")) {
      this.org = JSON.parse(sessionStorage.getItem("organization"));
      return await this.org;
    } else {
      let org;
      const value = await this.getOrganization()
        .toPromise()
        .then(async ({ data }: any) => {
          org = new MOrganization(data);
          this.setOrg(org);
        });
      return await org;
    }
  }

  public async getCurrentProcesses() {
    if (this.processes) {
      return await this.processes;
      // } else if (sessionStorage.getItem("processes")) {
      //   this.processes = JSON.parse(sessionStorage.getItem("processes"))
      //   return await this.processes
    } else {
      let processes;
      await this.getProcesses()
        .toPromise()
        .then(async ({ data }: any) => {
          this.processes = [];
          data.forEach(process => {
            this.processes.push(new MProcess(process));
          });
          // sessionStorage.setItem("processes", JSON.stringify(this.processes))
          processes = this.processes;
        });
      return await processes;
    }
  }

  /**
   * @author Carlos Albornoz
   * @name isValid
   * @description Devuelve la validez de un usuario
   * @returns boolean
   */
  public isValid() {
    if (!this.user) {
      if (!localStorage.getItem("activeUser")) {
        return false;
      }
      let activeUser: MUser = JSON.parse(localStorage.getItem("activeUser"));
      this.setUser(activeUser);
      if (!this.user.valid) {
        return false;
      }
    } else if (!this.user.valid) {
      return false;
    }
    return true;
  }

  /**
   * @author Carlos Albornoz
   * @name isLoggedIn
   * @description Verifica si hay un usuario activo
   */
  public isLoggedIn() {
    if (!this.user) {
      if (!sessionStorage.getItem("activeUser")) {
        return false;
      } else {
        let activeUser: MUser = JSON.parse(
          sessionStorage.getItem("activeUser")
        );
        this.setUser(activeUser);
      }
    }
    return true;
  }

  /**
   * @author Carlos Albornoz
   * @name logIn
   * @description Permite al usuario iniciar sesión en el sistema
   * @param user Nombre de Usuario
   * @param pass Contraseña del Usuario
   * @param remember Si el usuario desea ser recordado o no
   */
  public logIn(user: string, pass: string, remember: boolean = false) {
    let header = new HttpHeaders({
      "Content-Type": "application/json",
      Accept: "application/json"
    });
    let body = JSON.stringify({
      username: user,
      password: pass,
      remember_me: remember
    });
    return this.httpClient.post(environment.apiUrl + "/api/auth/login", body, {
      headers: header
    });
  }

  /**
   * @author Carlos Albornoz
   * @name logOut
   * @description Metodo para cerrar la sesion
   */
  public logOut() {
    this.user = null;
    this._global.user = null;
    localStorage.removeItem("activeUser");
    sessionStorage.removeItem("activeUser");
    this.getCurrentUser.next(null);
  }

  public setPass(pass: string) {
    this.pass = pass;
  }

  public getPass() {
    return this.pass;
  }

  /**
   * @author Carlos Albornoz
   * @name getUser
   * @description Una vez iniciada la sesión, se utiliza para obtener la informacion del usuario
   * @param token Token del Usuario
   */
  public getUser(token: string) {
    let header = new HttpHeaders({
      Authorization: "Bearer " + token,
      Accept: "application/json",
      "Content-Type": "application/json"
    });
    return this.httpClient.get(environment.apiUrl + "/api/auth/user", {
      headers: header
    });
  }

  /**
   * @author Carlos Albornoz
   * @name getMenuItems
   * @description Obtiene las opciones de menu del usuario
   * @param token El token del usuario al ingresar
   */
  public getMenuItems(token: string) {
    let header = new HttpHeaders({
      Authorization: "Bearer " + token,
      Accept: "application/json",
      "Content-Type": "application/json"
    });
    return this.httpClient.get(environment.apiUrl + "/api/profile/menu", {
      headers: header
    });
  }

  /**
   * @author Carlos Albornoz
   * @name getMenuItemsByToolboxId
   * @description Obtiene las opciones de menu del usuario segun el toolbox activo
   * @param token El token del usuario al ingresar
   */
  public getMenuItemsByToolboxId(token: string, id: number = 1) {
    let header = new HttpHeaders({
      Authorization: "Bearer " + token,
      Accept: "application/json",
      "Content-Type": "application/json"
    });
    return this.httpClient.get(environment.apiUrl + "/api/toolbox/menu/" + id, {
      headers: header
    });
  }

  /**
   * @author Carlos Albornoz
   * @name getQuestions
   * @description Obtiene las preguntas de seguridad del sistema
   */
  public getQuestions() {
    return this.httpClient.get(
      environment.apiUrl + "/api/profile/question-list",
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getUserQuestions
   * @description Obtiene las preguntas de seguridad del usuario
   */
  public getUserQuestions() {
    return this.httpClient.get(environment.apiUrl + "/api/profile/questions", {
      headers: this.header()
    });
  }

  /**
   * @author Carlos Albornoz
   * @name setUserQuestions
   * @description Obtiene las preguntas de seguridad del usuario
   */
  public setUserQuestions(questions: SecurityQuestion[]) {
    let body = JSON.stringify({
      opciones: questions
    });
    return this.httpClient.put(
      environment.apiUrl + "/api/profile/questions",
      body,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name setProfileInfo
   * @description Actualiza la informacion de un usuario
   * @param newName El nuevo nombre del usuario
   * @param newEmail El nuevo email del usuario
   */
  public setProfileInfo(
    newName: string,
    newEmail: string,
    newAvatar: string = null
  ) {
    let data;
    if (newAvatar) {
      data = {
        name: newName,
        email: newEmail,
        avatar: newAvatar
      };
    } else {
      data = {
        name: newName,
        email: newEmail
      };
    }
    let body = JSON.stringify(data);
    return this.httpClient.put(environment.apiUrl + "/api/profile/edit", body, {
      headers: this.header()
    });
  }

  /**
   * @author Carlos Albornoz
   * @name setPassword
   * @description Peticion para cambiar la contraseña de un usuario
   */
  public setPassword(current: string, newpass: string, confirm: string) {
    let body = JSON.stringify({
      current_password: current,
      password: newpass,
      password_confirmation: confirm
    });
    return this.httpClient.put(
      environment.apiUrl + "/api/profile/password",
      body,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name resetPassword
   * @param user Nombre de cuenta del usuario
   * @param pass La nueva contraseña a utilizar
   * @param confirm La confirmacion de la contraseña
   * @param token Token de Validacion
   */
  public resetPassword(
    user: string,
    pass: string,
    confirm: string,
    token: string
  ) {
    let body = JSON.stringify({
      username: user,
      password: pass,
      password_confirmation: confirm,
      token: token
    });
    let header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json"
    });
    return this.httpClient.post(
      environment.apiUrl + "/api/auth/password/reset",
      body,
      { headers: header }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getValidationQuestions
   * @description Obtiene 2 preguntas de un usuario con el fin de validarlas
   * @param username Nombre de usuario de quien se buscaran las preguntas
   */
  public getUserUsername(email: string) {
    let body = JSON.stringify({
      forgot_all: true,
      email: email
    });
    let header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json"
    });
    return this.httpClient.post(
      environment.apiUrl + "/api/auth/password/account",
      body,
      { headers: header }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getValidationQuestions
   * @description Obtiene 2 preguntas de un usuario con el fin de validarlas
   * @param username Nombre de usuario de quien se buscaran las preguntas
   */
  public getValidationQuestions(username: string) {
    let body = JSON.stringify({
      username: username
    });
    let header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json"
    });
    return this.httpClient.post(
      environment.apiUrl + "/api/auth/password/account",
      body,
      { headers: header }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name validateQuestions
   * @description Valido 2 preguntas para obtener un token
   * @param questions Mandar 2 preguntas con sus respectivas respuestas
   */
  public validateQuestions(username: string, questions: SecurityQuestion[]) {
    let body = JSON.stringify({
      username: username,
      opciones: questions
    });
    let header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json"
    });
    return this.httpClient.post(
      environment.apiUrl + "/api/auth/valid-questions",
      body,
      { headers: header }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getOrganizationUser
   * @description Retorna la empresa a la que esta afiliado el usuario
   */
  public getOrganization() {
    return this.httpClient.get(environment.apiUrl + "/api/organizacion", {
      headers: this.header()
    });
  }

  /**
   * @author Carlos Albornoz
   * @name getAllOrganizationUsers
   * @description Retorna los usuarios de la organizacion al que el usuario esta afiliado
   */
  public getAllOrganizationUsers() {
    return this.httpClient.get(
      environment.apiUrl + "/api/organizacion/usuario",
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getOrganizationUser
   * @description Retorna un usuario afiliado a la organizacion a la que el usuario actual esta afiliado
   */
  public getOrganizationUser(userId: string) {
    return this.httpClient.get(
      environment.apiUrl + "/api/organizacion/usuario/" + userId,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name createOrganizationUser
   * @description Crea un usuario para la organizacion a la que el usuario actual esta afiliado
   */
  public createOrganizationUser(user: OrganizationUser) {
    let body = JSON.stringify(user);
    return this.httpClient.post(
      environment.apiUrl + "/api/organizacion/usuario",
      body,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name updateOrganizationUser
   * @description Actualiza a un usuario afiliado a la organizacion a la que el usuario actual esta afiliado
   */
  public updateOrganizationUser(user: OrganizationUser) {
    let body = JSON.stringify(user);
    return this.httpClient.put(
      environment.apiUrl + "/api/organizacion/usuario",
      body,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getOrganizationUserById
   * @description Retorna un usuario de una organizacion en base a su id
   */
  public getOrganizationUserById(id: number, token?: string) {
    if (this.user) {
      return this.httpClient.get(
        environment.apiUrl + "/api/organizacion/usuario/" + id,
        { headers: this.header() }
      );
    } else {
      let header = new HttpHeaders({
        Authorization: "Bearer " + token,
        Accept: "application/json",
        "Content-Type": "application/json"
      });
      return this.httpClient.get(
        environment.apiUrl + "/api/organizacion/usuario/" + id,
        { headers: header }
      );
    }
  }

  /**
   * @author Carlos Albornoz
   * @name deleteOrganizationUser
   * @description Elimina un usuario afiliado a la organizacion a la que el usuario actual esta afiliado
   */
  public deleteOrganizationUser(userId: string) {
    return this.httpClient.delete(
      environment.apiUrl + "/api/organizacion/usuario/" + userId,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name updateUsersMassively
   * @param users El id de los usuarios a actualizar junto con los permisos que se les asignaran
   * @description Actualiza los permisos de los usuarios en bulto
   */
  public updateUsersMassively(users: OrganizationUserMassive[]) {
    let data = {
      usuarios: users
    };
    let body = JSON.stringify(data);
    return this.httpClient.put(
      environment.apiUrl + "/api/organizacion/usuario/permiso_masivo",
      body,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getProcesses
   * @description Retorna los procesos de a los que tiene acceso el usuario
   */
  public getProcesses() {
    return this.httpClient.get(environment.apiUrl + "/api/proceso", {
      headers: this.header()
    });
  }

  /**
   * @author Carlos Albornoz
   * @name getProcessById
   * @description Retorna un proceso segun el id
   */
  public getProcessById(id: number) {
    return this.httpClient.get(environment.apiUrl + "/api/proceso/" + id, {
      headers: this.header()
    });
  }

  /**
   * @author Carlos Albornoz
   * @name createProcess
   * @param name Nombre del proceso a crear
   * @description Crea un nuevo proceso
   */
  public createProcess(name: string) {
    let body = JSON.stringify({
      nombre: name
    });
    return this.httpClient.post(environment.apiUrl + "/api/proceso", body, {
      headers: this.header()
    });
  }

  /**
   * @author Carlos Albornoz
   * @name updateProcess
   * @param process Interfaz del proceso a actualziar
   * @description Actualiza la informacion de un proceso
   */
  public updateProcess(process: Process) {
    let body = JSON.stringify({
      nombre: process.nombre,
      id: process.id
    });
    return this.httpClient.put(environment.apiUrl + "/api/proceso", body, {
      headers: this.header()
    });
  }

  /**
   * @author Carlos Albornoz
   * @name deleteProcess
   * @description Elimina un proceso en funcion de su id
   */
  public deleteProcess(id: number) {
    return this.httpClient.delete(environment.apiUrl + "/api/proceso/" + id, {
      headers: this.header()
    });
  }

  /**
   * @author Carlos Albornoz
   * @name getToolboxes
   * @description Obtienes un listado de los toolboxes del sistema
   */
  public getToolboxes() {
    return this.httpClient.get(
      environment.apiUrl + "/api/organizacion/toolboxes",
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getToolboxesById
   * @description Obtienes un toolbox en funcion de su Id
   */
  public getToolboxById(id: number, token: string = null) {
    return this.httpClient.get(environment.apiUrl + "/api/toolbox/" + id, {
      headers: this.header(token)
    });
  }

  public async getCurrentToolbox() {
    if (this.toolbox) {
      // console.log(this.toolbox)
      return await this.toolbox;
    } else if (sessionStorage.getItem("toolbox")) {
      this.toolbox = JSON.parse(sessionStorage.getItem("toolbox"));
      // console.log(this.toolbox)
      return await this.toolbox;
    } else {
      let toolbox;
      const value = await this.getToolboxById(1)
        .toPromise()
        .then(async ({ data }: any) => {
          // console.log(data)
          toolbox = new MToolboxDashboard(data);
          this.setToolbox(toolbox);
        });
      return await toolbox;
    }
  }

  /**
   * @author Carlos Albornoz
   * @name setToolbox
   * @description Guarda el toolbox actual para que este disponible para toda la aplicacion
   */
  public setToolbox(newToolbox: MToolboxDashboard) {
    this.toolbox = newToolbox;
    sessionStorage.setItem("toolbox", JSON.stringify(this.toolbox));
  }

  /**
   * @author Carlos Albornoz
   * @name getModules
   * @description Obtiene un listado de los modulos activos de una organizacion
   */
  public getModules() {
    return this.httpClient.get(
      environment.apiUrl + "/api/organizacion/modulos",
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getModuleById
   * @description Obtiene un listado segun su id
   */
  public getModuleById(id: number) {
    return this.httpClient.get(environment.apiUrl + "/api/modulo/" + id, {
      headers: this.header()
    });
  }

  /**
   * @author Carlos Albornoz
   * @name getEvaluations
   * @description Obtiene un listado de las evaluaciones de los modulos de una organizacion
   */
  public getEvaluations() {
    return this.httpClient.get(environment.apiUrl + "/api/evaluacion", {
      headers: this.header()
    });
  }

  /**
   * @author Carlos Albornoz
   * @name getEvaluationById
   * @description Obtiene una evaluacion de modulo segun su id
   */
  public getEvaluationById(id: number) {
    return this.httpClient.get(environment.apiUrl + "/api/evaluacion/" + id, {
      headers: this.header()
    });
  }

  /**
   * @author Carlos Albornoz
   * @name getEvaluationsByModuleId
   * @description Obtiene el listado de evaluaciones de un modulo
   */
  public getEvaluationsByModuleId(id: number) {
    return this.httpClient.get(
      environment.apiUrl + "/api/evaluacion/modulo/" + id,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name createEvaluation
   * @description Crea una evaluacion nueva para un modulo
   */
  public createEvaluation(startDate: string, id, status: number) {
    let body = JSON.stringify({
      fecha_apertura: startDate,
      estatus: status,
      subversion_modulo_id: id
    });
    return this.httpClient.post(environment.apiUrl + "/api/evaluacion", body, {
      headers: this.header()
    });
  }

  /**
   * @author Carlos Albornoz
   * @name updateEvaluation
   * @description Actualiza una evaluacion nueva para un modulo
   */
  public updateEvaluation(
    evaluationId: number,
    startDate: string,
    finishDate: string,
    status: number,
    subversionId: number
  ) {
    let body = JSON.stringify({
      fecha_apertura: startDate,
      fecha_cierre: finishDate,
      estatus: status,
      subversion_modulo_id: subversionId
    });
    return this.httpClient.put(
      environment.apiUrl + "/api/evaluacion/" + evaluationId,
      body,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name deleteEvaluation
   * @description Elimina una evaluacion de modulo segun su id
   */
  public deleteEvaluation(id: number) {
    return this.httpClient.delete(
      environment.apiUrl + "/api/evaluacion/" + id,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getEvaluationSummary
   * @description Obtiene el resumen de la evaluacion de un modulo
   */
  public getEvaluationSummary(
    evaluationId: number,
    chapterId?: number,
    indexId?: number,
    itemId?: number
  ) {
    if (itemId) {
      return this.httpClient.get(
        environment.apiUrl +
          "/api/evaluacion/" +
          evaluationId +
          "/resumen/" +
          chapterId +
          "/" +
          indexId +
          "/" +
          itemId,
        { headers: this.header() }
      );
    } else if (indexId) {
      return this.httpClient.get(
        environment.apiUrl +
          "/api/evaluacion/" +
          evaluationId +
          "/resumen/" +
          chapterId +
          "/" +
          indexId,
        { headers: this.header() }
      );
    } else if (chapterId) {
      return this.httpClient.get(
        environment.apiUrl +
          "/api/evaluacion/" +
          evaluationId +
          "/resumen/" +
          chapterId,
        { headers: this.header() }
      );
    } else {
      return this.httpClient.get(
        environment.apiUrl + "/api/evaluacion/" + evaluationId + "/resumen",
        { headers: this.header() }
      );
    }
  }

  /**
   * @author Carlos Albornoz
   * @name getEvaluationSummaryByProcess
   * @description Obtiene el resumen de la evaluacion de un modulo
   */
  public getEvaluationSummaryByProcess(
    processId: number,
    evaluationId: number,
    chapterId?: number,
    indexId?: number,
    itemId?: number
  ) {
    if (itemId) {
      return this.httpClient.get(
        environment.apiUrl +
          "/api/evaluacion/" +
          evaluationId +
          "/resumen/" +
          chapterId +
          "/" +
          indexId +
          "/" +
          itemId +
          "?proceso_id=" +
          processId,
        { headers: this.header() }
      );
    } else if (indexId) {
      return this.httpClient.get(
        environment.apiUrl +
          "/api/evaluacion/" +
          evaluationId +
          "/resumen/" +
          chapterId +
          "/" +
          indexId +
          "?proceso_id=" +
          processId,
        { headers: this.header() }
      );
    } else if (chapterId) {
      return this.httpClient.get(
        environment.apiUrl +
          "/api/evaluacion/" +
          evaluationId +
          "/resumen/" +
          chapterId +
          "?proceso_id=" +
          processId,
        { headers: this.header() }
      );
    } else {
      return this.httpClient.get(
        environment.apiUrl +
          "/api/evaluacion/" +
          evaluationId +
          "/resumen?proceso_id=" +
          processId,
        { headers: this.header() }
      );
    }
  }

  /**
   * @author Carlos Albornoz
   * @name getEvaluationByVersionId
   * @descripcion Verifica si una version de modulo tiene una evaluacion activa y la retorna
   */
  public getEvaluationByVersionId(id: number) {
    return this.httpClient.get(
      environment.apiUrl + "/api/evaluacion/version/" + id,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getChaptersByVersion
   * @description Retorna el listado de capitulos segun la version de un modulo
   */
  public getChaptersByVersion(id: number) {
    return this.httpClient.get(
      environment.apiUrl + "/api/modulo/version/" + id + "/capitulos",
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name startEvaluation
   * @description Retorna toda la informacion concerniente al inicio de una evaluacion
   */
  public startEvaluation(id: number) {
    return this.httpClient.get(
      environment.apiUrl + "/api/evaluacion/version/" + id + "/iniciar",
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getEvaluationStructure
   * @description Obtiene el resumen de la evaluacion de un modulo
   */
  public getEvaluationStructure(
    moduleId: number,
    versionId: number,
    chapterId?: number,
    indexId?: number,
    itemId?: number
  ) {
    if (itemId) {
      return this.httpClient.get(
        environment.apiUrl +
          "/api/modulo/" +
          moduleId +
          "/estructura/version/" +
          versionId +
          "/" +
          chapterId +
          "/" +
          indexId +
          "/" +
          itemId,
        { headers: this.header() }
      );
    } else if (indexId) {
      return this.httpClient.get(
        environment.apiUrl +
          "/api/modulo/" +
          moduleId +
          "/estructura/version/" +
          versionId +
          "/" +
          chapterId +
          "/" +
          indexId,
        { headers: this.header() }
      );
    } else if (chapterId) {
      return this.httpClient.get(
        environment.apiUrl +
          "/api/modulo/" +
          moduleId +
          "/estructura/version/" +
          versionId +
          "/" +
          chapterId,
        { headers: this.header() }
      );
    } else {
      return this.httpClient.get(
        environment.apiUrl +
          "/api/modulo/" +
          moduleId +
          "/estructura/version/" +
          versionId,
        { headers: this.header() }
      );
    }
  }

  /**
   * @author Carlos Albornoz
   * @name createRequirementEvaluation
   * @description Api para comenzar la evaluacion de un requerimiento
   */
  public createRequirementEvaluation(
    evaluationId: number,
    requirementId: number,
    typeId: number,
    description1: string,
    description2: string,
    evidence: Evidence[]
  ) {
    let body = {
      requisito_id: requirementId,
      tipo_resultado_id: typeId,
      descripcion1: description1,
      descripcion2: description2,
      evidencias: evidence
    };
    return this.httpClient.post(
      environment.apiUrl + "/api/evaluacion/" + evaluationId + "/requisito",
      body,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name updateRequirementEvaluation
   * @description Api para actualizar la informacion de la evaluacion de un requerimiento
   */
  public updateRequirementEvaluation(
    evaluationId: number,
    requirementEvaluationId: number,
    requirementId: number,
    resultTypeId: number,
    description1: string,
    description2: string
  ) {
    let body = {
      id: requirementEvaluationId,
      requisito_id: requirementId,
      tipo_resultado_id: resultTypeId,
      descripcion1: description1,
      descripcion2: description2
    };
    return this.httpClient.put(
      environment.apiUrl + "/api/evaluacion/" + evaluationId + "/requisito",
      body,
      { headers: this.header() }
    );
  }

  /**
   * @author Miguel Mendoza
   * @name getRequirementEvaluationEvidence
   * @description Obtiene las evidencia de un requerimiento
   */
  public getRequirementEvaluationEvidence(
    evaluationId: number,
    requirementId: number,
    evidence: Evidence[] = []
  ) {
    return this.httpClient.get(
      environment.apiUrl +
        "/api/evaluacion/" +
        evaluationId +
        "/requisito/" +
        requirementId,
      { headers: this.header() }
    );
  }

  /**
   * @author Miguel Mendoza
   * @name getRequirement
   * @description Obtiene un requerimiento
   */
  public getRequirement(
    evaluationId: number,
    chapterId: number,
    indexId: number,
    itemId: number,
    requirementId: number
  ) {
    return this.httpClient.get(
      environment.apiUrl +
        "/api/evaluacion/" +
        evaluationId +
        "/resumen/" +
        chapterId +
        "/" +
        indexId +
        "/" +
        itemId +
        "/" +
        requirementId,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name updateRequirementEvaluationEvidence
   * @description Actualiza las evidencias de una evaluacion
   */
  public updateRequiremtenEvaluationEvidence(
    evaluationId: number,
    requirementId: number,
    evidence: Evidence[]
  ) {
    let body = {
      evidencias: evidence
    };
    return this.httpClient.post(
      environment.apiUrl +
        "/api/evaluacion/" +
        evaluationId +
        "/requisito/" +
        requirementId,
      body,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getResultTypes
   * @description Retorna los tipos de resultado que puede tener una evaluacion de requerimiento
   */
  public getResultTypes() {
    return this.httpClient.get(environment.apiUrl + "/api/tipo-resultado", {
      headers: this.header()
    });
  }

  /**
   * @author Carlos Albornoz
   * @name getEvaluationHistory
   * @descripcion Retorna el listado de evaluaciones cerradas de un modulo
   */
  public getEvaluationHistory() {
    return this.httpClient.get(
      environment.apiUrl + "/api/evaluacion/historico",
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name resetEvaluations
   * @description Resetea las evaluaciones de una organizacion educativa
   */
  public resetEvaluations() {
    return this.httpClient.delete(
      environment.apiUrl + "/api/evaluacion/resetear-evaluaciones",
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getSourcesAndTakers
   * @description Retorna los tomadores y las fuentes de una evaluacion
   */
  public getSourcesAndTakers(evaluationId: number) {
    return this.httpClient.get(
      environment.apiUrl +
        "/api/evaluacion/" +
        evaluationId +
        "/fuente-tomador",
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name setSourcesAndTakers
   * @description Establece que fuentes e indicadores estan afiliados a una evaluacion
   */
  public setSourcesAndTakers(evaluationId: number, sources: Source[]) {
    let body = {
      fuente_tomador: sources
    };
    return this.httpClient.post(
      environment.apiUrl +
        "/api/evaluacion/" +
        evaluationId +
        "/fuente-tomador",
      body,
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getHelp
   * @description Retorna la respuesta de ayuda segun la ruta
   */
  public getHelp(url: string, toolboxId: number) {
    return this.httpClient.post(
      `${environment.apiUrl}/api/ayuda-sistema/search/${toolboxId}`,
      {
        url: url
      },
      { headers: this.header() }
    );
  }
  public getReportTypes(idToolbox = 1) {
    return this.httpClient
      .get(`${environment.apiUrl}/api/reporte/toolbox/${idToolbox}`, { headers: this.header() })
      .pipe(map((res: any) => res.data.map(item => new MReportType(item))));
  }

  /**
   * @author Carlos Albornoz
   * @name getReport
   * @description Retorne el reporte generado de una evaluacion segun su tipo
   */
  public getReport(url: string) {
    let header = new HttpHeaders({
      Authorization: "Bearer " + this.user.token,
      Accept: "application/pdf",
      "Content-Type": "application/json"
    });
    return this.httpClient.get(`${environment.apiUrl}/${url}`, {
      headers: header,
      responseType: "blob"
    });
    // .pipe(
    //     map((res:any) => res.data.map(item => new MReportType(item)))
    // )
  }

  /**
   * @author Carlos Albornoz
   * @name sendReminder
   * @description Envia un correo recordatorio al usuario encargado de una actividad de tratamiento
   */
  public sendReminder(nonConformityId: number, faseId: number, notes: string) {
    return this.httpClient.post(
      `${environment.apiUrl}/api/recordatorio/gnc/${nonConformityId}/fase/${faseId}/actividad-ejecutar`,
      { observaciones: notes },
      { headers: this.header() }
    );
  }
  public getSettings(parent: string, key: string) {
    return this.httpClient.get(`${environment.apiUrl}/api/settings/${parent}/${key}`);
  }
}
